import {
  getDealtPriceFromData,
  getNotationFromParams,
  shouldHideDealtPrice,
} from "./dealtPriceHelpers";
import { formatDealtPriceWithNotation } from "./dealtPriceFormatter";
import { resolveNotationFromParams } from "@/lib/currencySymbolHelper";
import {
  containsHyphenInQuote,
  formatDollarFractionString,
  formatDollarFractionZValueForSmallFractionDisplay,
  returnFractionString,
  ruleOfEights,
} from "@/lib/businessRules/dollarFractionBusinessRule";

/**
 * DealtPriceCellRenderer
 *
 * Displays the dealt price (feeAdjustedQuote) with notation suffix.
 * - Shows notation only when there is a value (e.g., "4.19996%")
 * - Does NOT show notation as placeholder when empty
 * - Hides dealt price for structures with DealtAway status on OP
 * - Resolves "$" notation to actual currency symbol (£, €, ¥, AUD, etc.)
 *
 * Works in conjunction with dealtPriceValueFormatter for copy/paste support.
 */
const DealtPriceCellRenderer = (props: any) => {
  // Check if dealt price should be hidden (DealtAway status on structures)
  if (shouldHideDealtPrice(props, props.data?.status)) {
    return (
      <div
        data-testid="dealtPriceContainer"
        id="dealtPriceContainer"
        className="justify-center text-center"
        style={{ width: "100%" }}
      ></div>
    );
  }

  // Get the dealt price to display (ER level or block level)
  const dealtPriceToReturn = getDealtPriceFromData(props.data || {});

  // Get notation and resolve "$" to actual currency symbol
  const notation = getNotationFromParams(props);
  const displayNotation = resolveNotationFromParams(notation, props);

  // Handle $fr notation: convert Z-value digit to superscript fraction (e.g., "116-023" → "116-02 ³⁄₈")
  if (
    displayNotation === "$fr" &&
    dealtPriceToReturn &&
    typeof dealtPriceToReturn === "string" &&
    containsHyphenInQuote(dealtPriceToReturn)
  ) {
    const fractionStr = formatDollarFractionString(dealtPriceToReturn);
    const valueAfterHyphen = returnFractionString(dealtPriceToReturn, false);

    if (valueAfterHyphen.length === 3) {
      const quoteDisplayValue = fractionStr.slice(0, -1);
      const zValueDecimal = formatDollarFractionZValueForSmallFractionDisplay(
        dealtPriceToReturn,
        ruleOfEights,
      );
      return (
        <div
          data-testid="dealtPriceContainer"
          id="dealtPriceContainer"
          className="justify-center text-center"
          style={{ width: "100%" }}
        >
          {quoteDisplayValue}{zValueDecimal}
        </div>
      );
    }

    return (
      <div
        data-testid="dealtPriceContainer"
        id="dealtPriceContainer"
        className="justify-center text-center"
        style={{ width: "100%" }}
      >
        {fractionStr}
      </div>
    );
  }

  // For all other notations, use the existing string formatter
  const formattedValue = formatDealtPriceWithNotation(
    dealtPriceToReturn,
    displayNotation,
  );

  return (
    <div
      data-testid="dealtPriceContainer"
      id="dealtPriceContainer"
      className="justify-center text-center"
      style={{ width: "100%" }}
    >
      {formattedValue}
    </div>
  );
};

export default DealtPriceCellRenderer;
